import { useState } from 'react';

import { useBottomSpaceVar } from '#hooks/useBottomSpaceVar';
import { useTypingFieldFocused } from '#hooks/useTypingFieldFocused';
import type { LastSystemActor, SessionRoomEntry } from '../model/roomSessions';
import { useSessionRoomStore } from '../model/sessionRoomStore';
import type { SessionRoomPhase } from './RoomChrome';
import { SessionRoomPill } from './SessionRoomPill';

type SessionRoomBarProps = {
  /**
   * Экран переписки (только телефон): край свой – по умолчанию верх, под шапкой, подальше от поля
   * ввода, – и переносится, как везде (02.10.2026).
   */
  inChatThread?: boolean;
  session: SessionRoomEntry;
  phase: SessionRoomPhase;
  onExpand: () => void;
  onFullscreen?: (() => void) | null;
  onClose?: () => void;
  onOpenSessionSettings?: () => void;
  lastMessageAuthor?: string | null;
  lastSystemActor?: LastSystemActor | null;
};

/** Мобильная мини-версия использует те же кнопки и владельца голоса, что десктопная. */
export const SessionRoomBar = ({ inChatThread = false, ...props }: SessionRoomBarProps) => {
  const mobileAnchor = useSessionRoomStore((state) => state.mobileAnchor);
  const chatAnchor = useSessionRoomStore((state) => state.chatAnchor);
  const setChatAnchor = useSessionRoomStore((state) => state.setChatAnchor);
  const [barNode, setBarNode] = useState<HTMLDivElement | null>(null);
  const typing = useTypingFieldFocused();
  /*
    Пока человек печатает, полоса уезжает наверх, под шапку переписки (её высоту учитывает сам
    верхний край), а не пропадает (01.10.2026).

    Раньше она пряталась целиком: внизу её место занимают поле ввода и клавиатура, и в чате
    сессия исчезала ровно в тот момент, когда о ней пишут. Наверху ей ничто не мешает, а
    выбранный человеком край не трогаем – клавиатура убралась, полоса вернулась туда, где стояла.
  */
  /*
    В переписке до 02.10.2026 полоса стояла под шапкой намертво: снизу её место – поле ввода, и
    перенести её было нельзя вовсе. Владелец попросил переносить и там: край у переписки свой
    (по умолчанию верх), а внизу полоса встаёт над полем ввода – страница оставляет под неё место
    тем же резервом, что и везде.
  */
  const anchor = typing ? 'top' : inChatThread ? chatAnchor : mobileAnchor;
  // Резерв измеряется по настоящему положению полосы; у верхнего края резервировать нечего.
  useBottomSpaceVar('--voice-bar-space', anchor === 'bottom' ? barNode : null);

  return (
    <SessionRoomPill
      {...props}
      surface="bar"
      anchor={anchor}
      {...(inChatThread ? { dragAnchor: chatAnchor, onDragAnchor: setChatAnchor } : {})}
      onNodeChange={setBarNode}
    />
  );
};
